import React, { memo, useEffect, useRef } from "react";

/**
 * The progress log, drawn.
 *
 * It replaces a Material React Table - a virtualised table, its toolbar, its
 * head cells and their tooltips, a detail panel per row - with one canvas. The
 * table was the last heavy thing in the panel: every progress message put React
 * through a commit that rebuilt column models and rows, and a /24 spent about a
 * second of main thread in it.
 *
 * Nothing here re-renders. The component takes the same `source` ref the tiles
 * read, keeps its own scroll, hover and expansion in refs, and paints the rows
 * that are actually on screen inside a requestAnimationFrame loop.
 *
 * The scrolling is the browser's own: a spacer as tall as the content sits in a
 * scroll container and the canvas is sticky inside it, so the wheel, the
 * trackpad and the scrollbar behave as they always did - only the painting is
 * ours.
 *
 * It is drawn in the dashboard's language: the card's surface and hairlines,
 * the accent for what is running, the rows' marks in a gutter, the display face
 * for names and the mono face for times.
 */

/** the draw ceiling - text at 30fps reads as live without redrawing per frame */
const FRAME_MS = 33;

const ROW_H = 36;
const LINE_H = 20;
const HEAD_H = 36;
const PAD_X = 14;
const RAIL_X = 10;
const RAIL_W = 3;
const HOST_X = 24;
const MSG_X = 150;
const TIME_W = 62;

/**
 * The palette and the faces, read from the Orion tokens the panel carries.
 *
 * The log is a card of the dashboard's, so it takes the dashboard's own values
 * rather than a set of its own: the plate's surface and hairlines, the ink the
 * text is mixed from, the accent for what is happening now. The two outcome
 * colours are the tiles' own, so the block above and the list below agree about
 * what green and red mean.
 */
function readTheme(el) {
  const s = getComputedStyle(el);
  const token = (name, fallback) => (s.getPropertyValue(name) || "").trim() || fallback;
  const ink = token("--ink", "255,255,255");
  const accentRgb = token("--accent-rgb", "246,64,13");
  const display = token("--font-display", '"Instrument Sans", Inter, system-ui, sans-serif');
  const mono = token("--font-mono", "ui-monospace, SFMono-Regular, Menlo, monospace");

  return {
    key: `${token("--card", "")}|${ink}|${accentRgb}`,
    card: token("--card", "rgb(32,32,32)"),
    line: token("--line", `rgba(${ink},0.08)`),
    lineStrong: token("--line-strong", `rgba(${ink},0.16)`),
    text: token("--text", `rgba(${ink},0.92)`),
    text90: token("--text-90", `rgba(${ink},0.9)`),
    text72: token("--text-72", `rgba(${ink},0.72)`),
    text55: token("--text-55", `rgba(${ink},0.55)`),
    accent: token("--accent-text", "#ff5e19"),
    accentRgb,
    ink,
    wash: `rgba(${ink}, 0.05)`,
    washStrong: `rgba(${ink}, 0.08)`,
    pass: "rgb(71,148,72)",
    fail: "rgb(214,57,100)",
    /* the dashboard's three faces: mono for figures and time, the display face
       for names, Inter for prose */
    eyebrow: `10px ${mono}`,
    mono: `10.5px ${mono}`,
    name: `500 13px ${display}`,
    prose: "11.5px Inter, system-ui, sans-serif",
  };
}

/** What a host's row says: its state, its last line and when that arrived. */
export function rowFor(tree, host) {
  const msgs = tree[host] || [];
  const last =
    msgs.find(m => m.message_type === "status" || m.message_type === "success" || m.message_type === "failure") ||
    msgs[0] ||
    {};

  const state =
    last.message_type === "success" ? "pass" :
    last.message_type === "failure" ? "fail" : "run";

  const text =
    last.message_type === "success" ? "Scan Completed Successfully" :
    last.message || (last.message_type === "start" ? "Scan Started" : "");

  return { host, state, text, at: last.timestamp || 0, count: msgs.length };
}

function clockOf(ms) {
  if (!ms) return "";
  const d = new Date(ms);
  const p = n => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function ellipsis(ctx, text, room) {
  if (room <= 0) return "";
  if (ctx.measureText(text).width <= room) return text;
  let cut = text.length - 1;
  while (cut > 1 && ctx.measureText(`${text.slice(0, cut)}...`).width > room) cut--;
  return `${text.slice(0, cut)}...`;
}

function roundRect(ctx, x, y, w, h, r) {
  const rad = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}

/** A row's state, as the rail the dashboard draws its plates' edges with. */
function drawRail(ctx, x, y, h, state, theme) {
  ctx.fillStyle =
    state === "pass" ? theme.pass :
    state === "fail" ? theme.fail : theme.accent;
  roundRect(ctx, x, y, RAIL_W, h, RAIL_W / 2);
  ctx.fill();
}

/** Letter-spaced small text - the dashboard's eyebrow, drawn by hand. */
function tracked(ctx, text, x, y, spacing) {
  let cursor = x;
  for (const ch of text) {
    ctx.fillText(ch, cursor, y);
    cursor += ctx.measureText(ch).width + spacing;
  }
  return cursor - spacing - x;
}

function fit(canvas, width, height) {
  const dpr = window.devicePixelRatio || 1;
  const w = Math.round(width * dpr);
  const h = Math.round(height * dpr);
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext("2d");
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

function ScanProgressLog({ source }) {
  const scrollRef = useRef(null);
  const spacerRef = useRef(null);
  const canvasRef = useRef(null);
  const searchRef = useRef(null);

  /* everything the drawing needs that React must not hear about */
  const viewRef = useRef({
    hover: -1,
    expanded: null,
    filter: "",
    rows: [],          // {host, y, h, expanded} for the slice on screen
    layout: [],        // {host, top, h} for every row that passes the filter
    total: 0,
  });

  useEffect(() => {
    const scroller = scrollRef.current;
    const spacer = spacerRef.current;
    const canvas = canvasRef.current;
    if (!scroller || !spacer || !canvas) return undefined;

    let raf = 0;
    let lastPaint = 0;
    let lastSig = "";
    let theme = readTheme(scroller);

    const measure = (s) => {
      const view = viewRef.current;
      const filter = view.filter.toLowerCase();
      const layout = [];
      let top = HEAD_H;

      for (const host of s.keys) {
        if (filter) {
          const row = rowFor(s.tree, host);
          if (!host.toLowerCase().includes(filter) && !row.text.toLowerCase().includes(filter)) continue;
        }
        const open = view.expanded === host;
        const lines = open ? Math.min((s.tree[host] || []).length, 40) : 0;
        const h = ROW_H + (open ? lines * LINE_H + 10 : 0);
        layout.push({ host, top, h, open, lines });
        top += h;
      }

      view.layout = layout;
      view.total = top + 8;
      return view;
    };

    const draw = (now) => {
      const s = source.current;
      const view = viewRef.current;
      const width = scroller.clientWidth;
      const height = scroller.clientHeight;
      if (!s || width <= 0 || height <= 0) return;

      const ctx = fit(canvas, width, height);
      if (canvas.style.height !== `${height}px`) canvas.style.height = `${height}px`;
      const scrollTop = scroller.scrollTop;

      ctx.clearRect(0, 0, width, height);
      ctx.textBaseline = "middle";

      /* the rows are drawn into the box below the head and clipped to it, so a
         row scrolling past cannot climb over the head that names it */
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, HEAD_H, width, Math.max(0, height - HEAD_H));
      ctx.clip();

      const rows = [];
      const timeX = width - PAD_X;

      /* One sweep for the frame, shared by every row still being read: the
         dashboard shows work in flight as light crossing the plate, so a
         running host carries the same pass of the accent rather than a blinking
         mark of its own. */
      const cycle = (now / 1600) % 1;
      const sweepAt = -200 + cycle * (width + 400);
      const sweep = ctx.createLinearGradient(sweepAt, 0, sweepAt + 200, 0);
      sweep.addColorStop(0, `rgba(${theme.accentRgb}, 0)`);
      sweep.addColorStop(0.5, `rgba(${theme.accentRgb}, 0.11)`);
      sweep.addColorStop(1, `rgba(${theme.accentRgb}, 0)`);

      for (const item of view.layout) {
        const y = item.top - scrollTop;
        if (y + item.h < HEAD_H) continue;
        if (y > height) break;

        const row = rowFor(s.tree, item.host);
        const hovered = view.hover === item.top;
        rows.push({ host: item.host, y, h: item.h });

        /* the row's ground: a wash under the pointer or an open row, the sweep
           under one still running, nothing under one that has answered */
        if (hovered || item.open) {
          ctx.fillStyle = item.open ? theme.washStrong : theme.wash;
          roundRect(ctx, 6, y + 2, width - 12, ROW_H - 4, 9);
          ctx.fill();
        } else if (row.state === "run") {
          ctx.fillStyle = sweep;
          roundRect(ctx, 6, y + 2, width - 12, ROW_H - 4, 9);
          ctx.fill();
        }

        drawRail(ctx, RAIL_X, y + 9, ROW_H - 18, row.state, theme);

        ctx.textAlign = "left";
        ctx.font = theme.name;
        ctx.fillStyle = row.state === "run" ? theme.text : theme.text90;
        ctx.fillText(ellipsis(ctx, row.host, MSG_X - HOST_X - 12), HOST_X, y + ROW_H / 2);

        ctx.font = theme.prose;
        ctx.fillStyle = row.state === "fail" ? theme.text72 : theme.text55;
        ctx.fillText(ellipsis(ctx, row.text, timeX - MSG_X - TIME_W), MSG_X, y + ROW_H / 2);

        ctx.font = theme.mono;
        ctx.fillStyle = row.state === "run" ? theme.accent : theme.text55;
        ctx.textAlign = "right";
        ctx.fillText(row.state === "run" ? "LIVE" : clockOf(row.at), timeX, y + ROW_H / 2);

        /* what the row was holding: its own lines, newest first, quoted off a
           hairline rather than boxed - the dashboard's rules do this work */
        if (item.open) {
          const msgs = (s.tree[item.host] || []).slice(0, item.lines);
          let ly = y + ROW_H + 2;

          ctx.fillStyle = theme.line;
          ctx.fillRect(HOST_X, y + ROW_H + 2, 1, item.h - ROW_H - 8);

          for (const m of msgs) {
            const kind =
              m.message_type === "failure" ? theme.fail :
              m.message_type === "success" ? theme.pass : theme.text72;

            ctx.fillStyle = kind;
            ctx.beginPath();
            ctx.arc(HOST_X + 0.5, ly + LINE_H / 2, 2, 0, Math.PI * 2);
            ctx.fill();

            ctx.textAlign = "left";
            ctx.font = theme.mono;
            ctx.fillStyle = theme.text55;
            ctx.fillText(clockOf(m.timestamp), HOST_X + 12, ly + LINE_H / 2);

            ctx.font = theme.prose;
            ctx.fillStyle = m.message_type === "log" ? theme.text55 : kind;
            const text = m.message_type === "success" ? "Scan Completed Successfully" : (m.message || m.message_type || "");
            ctx.fillText(ellipsis(ctx, text, width - MSG_X - PAD_X), MSG_X, ly + LINE_H / 2);
            ly += LINE_H;
          }
        }

        ctx.fillStyle = theme.line;
        ctx.fillRect(PAD_X, y + item.h - 1, width - PAD_X * 2, 0.5);
      }

      ctx.restore();

      /* The head, last and over the card's own wash: the two columns as
         eyebrows, the tally in the display face at the right, and the rule
         under it carrying how much of the scan has answered - the dashboard
         puts its plates' progress in the rule rather than in a bar. */
      ctx.fillStyle = theme.card;
      ctx.fillRect(0, 0, width, HEAD_H);
      ctx.fillStyle = theme.wash;
      ctx.fillRect(0, 0, width, HEAD_H);

      ctx.font = theme.eyebrow;
      ctx.textAlign = "left";
      ctx.fillStyle = `rgba(${theme.ink}, 0.88)`;
      tracked(ctx, "HOST", PAD_X, HEAD_H / 2 - 1, 1.6);
      /* The message column goes unlabelled: what it holds says what it is - the
         line the host last sent - and the eyebrow over it only crowded a head
         that also carries the tally. */

      const settled = s.doneCount;
      const total = view.layout.length;
      ctx.textAlign = "right";
      ctx.font = theme.name;
      ctx.fillStyle = theme.text90;
      const tally = `${settled}`;
      ctx.fillText(tally, timeX - 26, HEAD_H / 2 - 1);
      ctx.font = theme.mono;
      ctx.fillStyle = theme.text55;
      ctx.fillText(`/${total}`, timeX, HEAD_H / 2 - 1);

      ctx.fillStyle = theme.line;
      ctx.fillRect(0, HEAD_H - 1.5, width, 1.5);
      if (total > 0) {
        ctx.fillStyle = theme.accent;
        ctx.globalAlpha = 0.85;
        ctx.fillRect(0, HEAD_H - 1.5, width * Math.min(1, settled / total), 1.5);
        ctx.globalAlpha = 1;
      }

      view.rows = rows;
    };

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      if (now - lastPaint < FRAME_MS) return;

      const s = source.current;
      if (!s) return;

      const view = viewRef.current;
      const sig = `${s.version}|${scroller.scrollTop}|${scroller.clientWidth}x${scroller.clientHeight}|${view.hover}|${view.expanded}|${view.filter}|${window.devicePixelRatio}`;
      const running = s.keys.length > s.doneCount;
      if (sig === lastSig && !running) return;

      if (sig !== lastSig) {
        const next = readTheme(scroller);
        if (next.key !== theme.key) theme = next;
        measure(s);
        const height = `${view.total}px`;
        if (spacer.style.height !== height) spacer.style.height = height;

        /* a tile or a click elsewhere asked for a host: bring it into view once */
        if (s.pendingScrollTo) {
          const target = view.layout.find(r => r.host === s.pendingScrollTo);
          s.pendingScrollTo = null;
          if (target) {
            view.expanded = target.host;
            measure(s);
            spacer.style.height = `${view.total}px`;
            scroller.scrollTop = Math.max(0, target.top - HEAD_H - 8);
          }
        }
      }

      lastPaint = now;
      lastSig = sig;
      draw(now);
    };

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [source]);

  const rowAt = (event) => {
    const view = viewRef.current;
    const rect = event.currentTarget.getBoundingClientRect();
    const y = event.clientY - rect.top;
    if (y < HEAD_H) return null;
    return view.rows.find(r => y >= r.y && y < r.y + r.h) || null;
  };

  return (
    <div className="scan-log">
      <div className="scan-log__bar">
        <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="currentColor"
            d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5Zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14Z"
          />
        </svg>
        <input
          ref={searchRef}
          className="scan-log__search"
          type="text"
          placeholder="Filter hosts"
          onChange={(event) => { viewRef.current.filter = event.target.value.trim(); }}
        />
      </div>

      <div
        className="scan-log__scroller noScroll"
        ref={scrollRef}
        onMouseMove={(event) => {
          const hit = rowAt(event);
          const view = viewRef.current;
          const row = hit ? view.layout.find(r => r.host === hit.host) : null;
          view.hover = row ? row.top : -1;
        }}
        onMouseLeave={() => { viewRef.current.hover = -1; }}
        onClick={(event) => {
          const hit = rowAt(event);
          if (!hit) return;
          const view = viewRef.current;
          view.expanded = view.expanded === hit.host ? null : hit.host;
        }}
      >
        <div className="scan-log__spacer" ref={spacerRef}>
          <canvas ref={canvasRef} className="scan-log__canvas" />
        </div>
      </div>
    </div>
  );
}

export default memo(ScanProgressLog);
